import { writeInt32, writeDouble } from './utils';
import { createDbfFile } from './dbfWriter';

export function writeShapefile(
  features: any[],
  geometryType: 'Point' | 'LineString' | 'Polygon',
): { shp: Blob; shx: Blob; dbf: Blob; prj: Blob } {
  const shapeTypeMap = {
    Point: 1,
    LineString: 3,
    Polygon: 5,
  };
  const shapeType = shapeTypeMap[geometryType];

  // Get spatial reference (wkid) from the first feature.
  const wkid = features[0]?.geometry?.spatialReference?.wkid || 3857;

  // Calculate buffer sizes.
  const { shpBufferSize } = calculateShapefileRequirements(
    features,
    geometryType,
  );
  const shpBuffer = new ArrayBuffer(shpBufferSize);
  const shpView = new DataView(shpBuffer);

  const shxBuffer = new ArrayBuffer(100 + features.length * 8);
  const shxView = new DataView(shxBuffer);

  const bbox = calculateBoundingBox(features, geometryType);

  writeShapefileHeader(shpView, shpBufferSize, shapeType, bbox);
  writeShapefileHeader(shxView, shxBuffer.byteLength, shapeType, bbox);

  let shpOffset = 100;
  let shxOffset = 100;

  features.forEach((feature, index) => {
    const recordInfo = writeShapeRecord(
      shpView,
      shxView,
      feature,
      index,
      geometryType,
      shpOffset,
      shxOffset,
    );
    shpOffset = recordInfo.newShpOffset;
    shxOffset = recordInfo.newShxOffset;
  });

  const dbfBlob = createDbfFile(features);
  const prjContent = getPrjContent(wkid);

  return {
    shp: new Blob([shpBuffer], { type: 'application/octet-stream' }),
    shx: new Blob([shxBuffer], { type: 'application/octet-stream' }),
    dbf: dbfBlob,
    prj: new Blob([prjContent], { type: 'text/plain' }),
  };
}

export function calculateShapefileRequirements(
  features: any[],
  geometryType: 'Point' | 'LineString' | 'Polygon',
): { shpBufferSize: number; totalPoints: number; totalParts: number } {
  let totalPoints = 0;
  let totalParts = 0;
  let shpBufferSize = 100; // header size

  features.forEach((feature) => {
    const geom = feature.geometry;
    shpBufferSize += 8; // record header

    if (geometryType === 'Point') {
      shpBufferSize += 20;
      totalPoints++;
    } else {
      const paths = (geom.type === 'polyline' ? geom.paths : geom.rings) || [];
      const currentParts = paths.length;
      const currentPoints = paths.flat().length;
      shpBufferSize += 44;
      shpBufferSize += currentParts * 4;
      shpBufferSize += currentPoints * 16;
      totalParts += currentParts;
      totalPoints += currentPoints;
    }
  });

  return { shpBufferSize, totalPoints, totalParts };
}

export function calculateBoundingBox(
  features: any[],
  geometryType: 'Point' | 'LineString' | 'Polygon',
): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  features.forEach((feature) => {
    const geom = feature.geometry;
    if (!geom) return;

    const extractCoords = (g: any): [number, number][] => {
      if (g.type === 'point' || g.type === 'Point') {
        return g.x !== undefined && g.y !== undefined ? [[g.x, g.y]] : [];
      }
      if (g.type === 'polyline' || g.type === 'LineString') {
        if (g.paths) {
          return g.paths.flatMap((path: number[][]) =>
            path.map(([x, y]) => [x, y]),
          );
        }
        if (g.coordinates) {
          return Array.isArray(g.coordinates[0])
            ? (g.coordinates as number[][]).map(([x, y]) => [x, y])
            : [[g.coordinates[0], g.coordinates[1]]];
        }
        return [];
      }
      if (g.type === 'polygon' || g.type === 'Polygon') {
        if (g.rings) {
          return g.rings.flatMap((ring: number[][]) =>
            ring.map(([x, y]) => [x, y]),
          );
        }
        if (g.coordinates) {
          return (g.coordinates[0] as number[][]).map(([x, y]) => [x, y]);
        }
        return [];
      }
      return [];
    };

    const coords = extractCoords(geom);
    coords.forEach(([x, y]) => {
      if (
        typeof x === 'number' &&
        typeof y === 'number' &&
        !isNaN(x) &&
        !isNaN(y)
      ) {
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x);
        maxY = Math.max(maxY, y);
      }
    });
  });

  if (!isFinite(minX)) {
    minX = -180;
    minY = -90;
    maxX = 180;
    maxY = 90;
  }

  return { minX, minY, maxX, maxY };
}

export function writeShapefileHeader(
  view: DataView,
  fileLength: number,
  shapeType: number,
  bbox: { minX: number; minY: number; maxX: number; maxY: number },
): void {
  writeInt32(view, 0, 9994, false);
  writeInt32(view, 24, fileLength / 2, false);
  writeInt32(view, 28, 1000, true);
  writeInt32(view, 32, shapeType, true);
  writeDouble(view, 36, bbox.minX, true);
  writeDouble(view, 44, bbox.minY, true);
  writeDouble(view, 52, bbox.maxX, true);
  writeDouble(view, 60, bbox.maxY, true);
  writeDouble(view, 68, 0, true);
  writeDouble(view, 76, 0, true);
  writeDouble(view, 84, 0, true);
  writeDouble(view, 92, 0, true);
}

export function writeShapeRecord(
  shpView: DataView,
  shxView: DataView,
  feature: any,
  index: number,
  geometryType: 'Point' | 'LineString' | 'Polygon',
  shpOffset: number,
  shxOffset: number,
): { newShpOffset: number; newShxOffset: number } {
  const geom = feature.geometry;
  if (!geom) {
    return { newShpOffset: shpOffset, newShxOffset: shxOffset };
  }

  const contentStart = shpOffset + 8;
  writeInt32(shpView, shpOffset, index + 1, false);

  let contentLength: number;
  let newShpOffset = shpOffset;

  if (geometryType === 'Point') {
    const x = geom.x || 0;
    const y = geom.y || 0;
    contentLength = 10;
    writeInt32(shpView, shpOffset + 4, contentLength, false);
    writeInt32(shpView, contentStart, 1, true);
    writeDouble(shpView, contentStart + 4, x, true);
    writeDouble(shpView, contentStart + 12, y, true);
    newShpOffset += 28;
  } else {
    const paths =
      geom.type === 'polyline'
        ? geom.paths
        : geom.type === 'polygon'
          ? geom.rings
          : geom.type === 'LineString'
            ? [geom.coordinates || []]
            : geom.type === 'Polygon'
              ? [geom.coordinates?.[0] || []]
              : [];
    const flatPaths: [number, number][] = [];
    paths.forEach((path: any) => {
      if (Array.isArray(path)) {
        path.forEach((coord: any) => {
          if (
            Array.isArray(coord) &&
            coord.length >= 2 &&
            typeof coord[0] === 'number' &&
            typeof coord[1] === 'number'
          ) {
            flatPaths.push([coord[0], coord[1]]);
          }
        });
      }
    });
    const numParts = paths.length;
    const numPoints = flatPaths.length;
    const bounds = flatPaths.reduce(
      (acc, [x, y]) => ({
        minX: Math.min(acc.minX, x),
        minY: Math.min(acc.minY, y),
        maxX: Math.max(acc.maxX, x),
        maxY: Math.max(acc.maxY, y),
      }),
      { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity },
    );
    if (!isFinite(bounds.minX)) {
      return { newShpOffset: shpOffset, newShxOffset: shxOffset };
    }
    contentLength = (44 + numParts * 4 + numPoints * 16) / 2;
    writeInt32(shpView, shpOffset + 4, contentLength, false);
    writeInt32(
      shpView,
      contentStart,
      geometryType === 'LineString' ? 3 : 5,
      true,
    );
    writeDouble(shpView, contentStart + 4, bounds.minX, true);
    writeDouble(shpView, contentStart + 12, bounds.minY, true);
    writeDouble(shpView, contentStart + 20, bounds.maxX, true);
    writeDouble(shpView, contentStart + 28, bounds.maxY, true);
    writeInt32(shpView, contentStart + 36, numParts, true);
    writeInt32(shpView, contentStart + 40, numPoints, true);
    let pointIndex = 0;
    paths.forEach((part: any, i: number) => {
      if (Array.isArray(part)) {
        writeInt32(shpView, contentStart + 44 + i * 4, pointIndex, true);
        pointIndex += part.length;
      }
    });
    let pointOffset = contentStart + 44 + numParts * 4;
    flatPaths.forEach(([x, y]) => {
      writeDouble(shpView, pointOffset, x, true);
      writeDouble(shpView, pointOffset + 8, y, true);
      pointOffset += 16;
    });
    newShpOffset += 8 + (44 + numParts * 4 + numPoints * 16);
  }
  writeInt32(shxView, shxOffset, shpOffset / 2, false);
  writeInt32(shxView, shxOffset + 4, contentLength, false);

  return { newShpOffset, newShxOffset: shxOffset + 8 };
}

export function getPrjContent(wkid = 3857): string {
  const projections: Record<number, string> = {
    3857: 'PROJCS["WGS_84_Pseudo_Mercator",GEOGCS["GCS_WGS_1984",DATUM["D_WGS_1984",SPHEROID["WGS_1984",6378137,298.257223563]],PRIMEM["Greenwich",0],UNIT["Degree",0.017453292519943295]],PROJECTION["Mercator"],PARAMETER["central_meridian",0],PARAMETER["false_easting",0],PARAMETER["false_northing",0],UNIT["Meter",1],PARAMETER["standard_parallel_1",0.0]]',
  };
  return projections[wkid] || projections[3857];
}
